<script setup lang="ts">
import type { ShortsOnboardingDismissReason } from '~/composables/shorts';

import {
	breakpointsTailwind,
	useBreakpoints,
	useEventListener
} from '@vueuse/core';

import { Button } from '~/components/ui/button';

import IconArrowOutward from '~icons/material-symbols-light/arrow-outward';
import IconShortsOnboardingScroll from '~icons/pilar/shorts-onboarding-scroll';
import IconShortsOnboardingSwipe from '~icons/pilar/shorts-onboarding-swipe';

const emit = defineEmits<{
	(e: 'dismiss', reason: ShortsOnboardingDismissReason): void;
}>();

const BACKGROUND_IMAGE_SRC = '/images/shorts/onboarding.webp';
const CTA_TESTID = 'shorts-onboarding-cta';
const KEYBOARD_DISMISS_KEYS = new Set([
	'ArrowUp',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	' ',
	'Spacebar'
]);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('lg');

const message = computed(() =>
	isMobile.value
		? 'Deslize verticalmente para navegar por uma seleção exclusiva de propriedades de alto padrão.'
		: 'Faça scroll para navegar por uma seleção exclusiva de propriedades de alto padrão.'
);

let previouslyFocused: HTMLElement | null = null;

function dismiss(reason: ShortsOnboardingDismissReason, event?: Event) {
	event?.preventDefault();
	event?.stopPropagation();
	emit('dismiss', reason);
}

function handleKeydown(event: KeyboardEvent) {
	if (event.key === 'Escape') {
		dismiss('keyboard_escape', event);
		return;
	}
	if (KEYBOARD_DISMISS_KEYS.has(event.key)) {
		dismiss('keyboard_arrow', event);
	}
}

// Capture phase ensures dismiss runs before PropertyReelsFeed's own key handlers.
useEventListener(window, 'keydown', handleKeydown, { capture: true });

onMounted(() => {
	previouslyFocused = (document.activeElement as HTMLElement) ?? null;
	nextTick(() => {
		document
			.querySelector<HTMLButtonElement>(`[data-testid="${CTA_TESTID}"]`)
			?.focus();
	});
});

onBeforeUnmount(() => {
	previouslyFocused?.focus?.();
});
</script>

<template>
	<Teleport to="body">
		<div
			class="fixed inset-0 z-210 flex items-center justify-center overflow-hidden bg-black"
			role="dialog"
			aria-modal="true"
			aria-labelledby="shorts-onboarding-title"
			data-testid="shorts-onboarding"
			@click="dismiss('overlay_click', $event)"
			@wheel="dismiss('scroll', $event)"
			@touchmove="dismiss('scroll', $event)"
		>
			<div
				class="relative z-10 flex flex-col items-center justify-center gap-8 p-8 lg:gap-11"
			>
				<component
					:is="
						isMobile ? IconShortsOnboardingSwipe : IconShortsOnboardingScroll
					"
					class="size-12 text-white"
					aria-hidden="true"
				/>

				<div
					class="flex w-[311px] flex-col items-center gap-6 text-center lg:w-[440px] lg:gap-11"
				>
					<div class="flex w-full flex-col items-center gap-2 lg:gap-3">
						<p
							id="shorts-onboarding-title"
							class="font-matter-sq text-[24px] font-normal leading-normal tracking-[-0.02em] text-white lg:text-[32px]"
						>
							Explore imóveis em Shorts
						</p>
						<p
							class="text-[16px] font-normal leading-normal text-white-dark-4 lg:text-[18px]"
						>
							{{ message }}
						</p>
					</div>

					<Button
						variant="primary-dark"
						:size="isMobile ? 'sm' : 'lg'"
						:is-client-only="false"
						skeleton-classes="w-full"
						class="w-full"
						:data-testid="CTA_TESTID"
						@click.stop="dismiss('cta')"
					>
						Explorar agora
						<IconArrowOutward class="size-5 lg:size-6" />
					</Button>
				</div>
			</div>
			<img
				:src="BACKGROUND_IMAGE_SRC"
				alt=""
				aria-hidden="true"
				class="pointer-events-none absolute inset-0 -z-10 size-full scale-125 object-cover blur-[50px] brightness-50"
			/>
		</div>
	</Teleport>
</template>
